import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./CustomGrid.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DescriptionIcon from "@mui/icons-material/Description";
import { useCompany } from "../../components/context/CompanyContext";
import {
  setFilterText,
  setFilterColumn,
  setSort,
  setPage,
  setAllFilters,
  setVisibleColumns,
  resetGridById,
} from "../../redux/customGridRedux";
import { useSelector, useDispatch } from "react-redux";
import { userRequest } from "../../requestMethods";

import CircularProgress from "@mui/material/CircularProgress";

// Operadores disponíveis por tipo de campo (definido fora do componente)
const FILTER_OPERATORS = {
  boolean: [
    { value: "isTrue", label: "é verdadeiro" },
    { value: "isFalse", label: "é falso" },
  ],
  text: [
    { value: "contains", label: "contém" },
    { value: "equals", label: "igual a" },
    { value: "startsWith", label: "começa com" },
    { value: "endsWith", label: "termina com" },
    { value: "isEmpty", label: "está vazio" },
    { value: "isNotEmpty", label: "não está vazio" },
  ],
  number: [
    { value: "contains", label: "contém" },
    { value: "equals", label: "=" },
    { value: "notEquals", label: "≠" },
    { value: "greaterThan", label: ">" },
    { value: "greaterThanOrEqual", label: "≥" },
    { value: "lessThan", label: "<" },
    { value: "lessThanOrEqual", label: "≤" },
    { value: "isEmpty", label: "está vazio" },
    { value: "isNotEmpty", label: "não está vazio" },
  ],
  date: [
    { value: "equals", label: "igual a" },
    { value: "notEquals", label: "diferente de" },
    { value: "greaterThan", label: "depois de" },
    { value: "greaterThanOrEqual", label: "a partir de" },
    { value: "lessThan", label: "antes de" },
    { value: "lessThanOrEqual", label: "até" },
    { value: "isEmpty", label: "está vazio" },
    { value: "isNotEmpty", label: "não está vazio" },
  ],
};

const formatValue = (value, format) => {
  if (value == null) return "";
  if (!format) return String(value);

  if (format === "BRL") {
    const number = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(number)) return value;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(number);
  }

  const date = new Date(value);
  if (!isNaN(date)) {
    const pad = (n) => String(n).padStart(2, "0");
    return format
      .replace("dd", pad(date.getDate()))
      .replace("MM", pad(date.getMonth() + 1))
      .replace("yyyy", date.getFullYear())
      .replace("HH", pad(date.getHours()))
      .replace("mm", pad(date.getMinutes()))
      .replace("ss", pad(date.getSeconds()));
  }
  return String(value);
};

export default function CustomGrid({
  gridId,
  rows,
  columns,
  getRowId = (r) =>
    r?.id ??
    r?._id ??
    r?.cartId ??
    r?.productId ??
    r?.uuid ??
    JSON.stringify(r),
  pageSize = 10,
  checkboxSelection = false,
  onSelectionChange,
  onRowClick,
  className = "",
  tableHeight = "900px",
  selectionField = null,
  selectionModel = [],
  defaultFilterText = "",
  defaultFilterColumn = "",
  serverSide = false,
  rowCount = null,
  onPageChangeServer = null,
  endpopintExport = null,
  objectExport = null,
  callExport = null,
}) {
  const dispatch = useDispatch();
  const gridState = useSelector((state) => state.customGrid[gridId] || {});

  // 1) Pegamos o valor cru do Redux — normalizando já no selector para garantir array
  const rawVisibleColumns = useSelector((state) => {
    const stored = state.customGrid?.[gridId]?.visibleColumns;
    return Array.isArray(stored) ? stored : [];
  });

  // 2) Normalizamos sempre como array usando useMemo (agora redundante, mas mantido)
  const reduxVisibleColumns = useMemo(() => {
    return Array.isArray(rawVisibleColumns) ? rawVisibleColumns : [];
  }, [rawVisibleColumns]);

  const [loading, setLoading] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const reduxFilterText =
    useSelector((state) => state.customGrid[gridId]?.filterText) || "";
  const reduxFilterColumn =
    useSelector((state) => state.customGrid[gridId]?.filterColumn) || "";
  const reduxAllFilters = useSelector(
    (state) => state.customGrid[gridId]?.allFilters || [],
  );
  const [filters, setFilters] = useState(() => {
    return reduxAllFilters.length > 0
      ? reduxAllFilters.map((f) => ({ ...f }))
      : [
          {
            field: defaultFilterColumn || "",
            operator: "contains",
            value: defaultFilterText || "",
          },
        ];
  });

  const filterText = gridState.filterText || "";
  const filterColumn = gridState.filterColumn || "";
  const sortField = gridState.sortField || null;
  const sortDir = gridState.sortDir || null;
  const page = gridState.page || 0;
  const [hoveredCol, setHoveredCol] = useState(null);

  const [internalRows, setInternalRows] = useState(
    Array.isArray(rows) ? rows : [],
  );
  const [selected, setSelected] = useState([]);
  const [colWidths, setColWidths] = useState(() =>
    columns.reduce(
      (acc, col) => ({ ...acc, [col.field]: col.width || 150 }),
      {},
    ),
  );

  // garante valor inicial seguro (array)
  const [visibleColumns, setLocalVisibleColumns] = useState(() => {
    return Array.isArray(reduxVisibleColumns) && reduxVisibleColumns.length > 0
      ? reduxVisibleColumns
      : columns.map((c) => c.field);
  });

  const { companyId } = useCompany();
  const [temFiltro, setTemFiltro] = useState();

  useEffect(() => {
    // se houver algo no redux, usa — desde que seja array
    if (Array.isArray(reduxVisibleColumns) && reduxVisibleColumns.length > 0) {
      setLocalVisibleColumns(reduxVisibleColumns);
    }
  }, [reduxVisibleColumns]);

  const toggleColumnVisibility = (field) => {
    // sempre trabalhe com uma cópia segura (array)
    const safeVisible = Array.isArray(visibleColumns)
      ? visibleColumns
      : columns.map((c) => c.field);

    const updated = safeVisible.includes(field)
      ? safeVisible.filter((f) => f !== field)
      : [...safeVisible, field];

    setLocalVisibleColumns(updated);

    // dispatch com payload seguro (array)
    dispatch(setVisibleColumns({ gridId, visibleColumns: updated }));
  };

  const getRowsExport = async () => {
    setLoading(true);
    try {
      let res, rowsExport;
      if (callExport === "get") {
        res = await userRequest.get(
          `${companyId}${endpopintExport}?limit=25000`,
        );
      }
      if (callExport === "post") {
        res = await userRequest.post(`${companyId}${endpopintExport}`, {
          limit: 25000,
        });
      }

      if (res?.data) {
        if (objectExport) {
          rowsExport = res.data.map((item) => objectExport(item));
        } else {
          rowsExport = res.data;
        }
      }
      setLoading(false);

      return rowsExport;
    } catch (error) {
      setLoading(false);
      return [];
    }
  };
  const handleAddFilter = () => {
    setFilters((prev) => [
      ...prev,
      { field: "", operator: "contains", value: "" },
    ]);
  };

  // 🧩 Remover linha de filtro
  const handleRemoveFilter = (index) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  // 🧹 Limpar todos os filtros
  const handleClearFilters = () => {
    setFilters([{ field: "", operator: "contains", value: "" }]);
    dispatch(resetGridById({ gridId }));
  };

  const handleFilterFieldChange = (index, value) => {
    setFilters((prev) => {
      const updated = [...prev];
      const fieldType = getFieldType(value);
      const defaultOperator = fieldType === "text" ? "contains" : "equals";
      updated[index] = {
        ...updated[index],
        field: value,
        operator: defaultOperator,
      };
      return updated;
    });

    if (index === 0) {
      dispatch(setFilterColumn({ gridId, filterColumn: value }));
    }
  };

  const handleFilterOperatorChange = (index, operator) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], operator };
    setFilters(newFilters);

    // ✅ usa o newFilters em vez de filters
    dispatch(setAllFilters({ gridId, filters: newFilters }));
  };

  const handleFilterValueChange = (index, value) => {
    const newFilters = [...filters];
    newFilters[index].value = value;
    setFilters(newFilters);

    // ✅ usa o newFilters em vez de filters
    dispatch(setAllFilters({ gridId, filters: newFilters }));
  };

  useEffect(() => {
    if (reduxAllFilters && reduxAllFilters.length > 0) {
      setFilters(reduxAllFilters.map((f) => ({ operator: "contains", ...f })));
    } else {
      setFilters([
        {
          field: reduxFilterColumn || defaultFilterColumn || "",
          operator: "contains",
          value: reduxFilterText || defaultFilterText || "",
        },
      ]);
    }
  }, [
    reduxAllFilters,
    reduxFilterColumn,
    reduxFilterText,
    defaultFilterColumn,
    defaultFilterText,
  ]);

  useEffect(() => {
    setInternalRows(Array.isArray(rows) ? rows : []);
  }, [rows]);
  useEffect(() => {
    const same =
      reduxAllFilters.length === filters.length &&
      reduxAllFilters.every(
        (f, i) => f.field === filters[i].field && f.value === filters[i].value,
      );

    if (!same) {
      dispatch(setAllFilters({ gridId, filters }));
    }
    setTemFiltro(hasFilters(filters));
  }, [filters, gridId, dispatch]);
  // useEffect(() => {
  //   const same =
  //     reduxAllFilters.length === filters.length &&
  //     reduxAllFilters.every(
  //       (f, i) => f.field === filters[i].field && f.value === filters[i].value
  //     );

  //   if (!same) {
  //     setFilters(reduxAllFilters.map((f) => ({ ...f })));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [reduxAllFilters]);
  const arraysEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (String(a[i]) !== String(b[i])) return false;
    }
    return true;
  };
  useEffect(() => {
    // normaliza selectionModel para array
    if (!Array.isArray(selectionModel)) {
      // só atualiza se for diferente do current
      setSelected((prev) => (arraysEqual(prev, []) ? prev : []));
      return;
    }

    let ids = [];
    if (selectionField) {
      const wanted = new Set(selectionModel.map((v) => String(v)));
      ids = (internalRows || [])
        .filter((r) => r && wanted.has(String(r[selectionField])))
        .map((r) => getRowId(r));
    } else {
      ids = selectionModel;
    }

    setSelected((prev) => (arraysEqual(prev, ids) ? prev : ids));
  }, [selectionModel, internalRows, selectionField]);

  useEffect(() => {
    // só aplica defaults quando não há valor salvo no redux para esse gridId
    const saved = gridState || {};
    const savedText = saved.filterText;
    const savedColumn = saved.filterColumn;

    if (!savedText && defaultFilterText) {
      dispatch(setFilterText({ gridId, filterText: defaultFilterText }));
    }
    if (!savedColumn && defaultFilterColumn) {
      dispatch(setFilterColumn({ gridId, filterColumn: defaultFilterColumn }));
    }
  }, [defaultFilterText, defaultFilterColumn, gridId, dispatch, gridState]);

  const normalizedColumns = useMemo(
    () =>
      columns.map((col) => ({
        sortable: true,
        headerName: col.headerName ?? col.header ?? "",
        field: col.field,
        dbField: col.dbField ?? col.field,
        type: col.type,
        format: col.format,
        renderCell: col.renderCell,
        align: col.align,
        headerAlign: col.headerAlign,
        width: colWidths[col.field] || 40,
        description: col.description ?? col.headerName,
        clickable: col.clickable ?? false,
      })),
    [columns, colWidths],
  );

  // Detecta o tipo do campo baseado no formato ou nos dados
  const getFieldType = useCallback(
    (field) => {
      const col = normalizedColumns.find(
        (c) => c.field === field || c.dbField === field,
      );
      if (!col) return "text";

      if (col.type === "boolean") return "boolean";

      if (col.format === "dd/MM/yyyy" || col.format === "dd/MM/yyyy HH:mm:ss") {
        return "date";
      }
      if (col.format === "BRL") {
        return "number";
      }

      // Verifica se os valores são numéricos
      const sampleValues = (rows || [])
        .slice(0, 10)
        .map((r) => r[col.dbField ?? col.field]);
      const allNumbers = sampleValues.every(
        (v) => v === null || v === undefined || !isNaN(Number(v)),
      );
      if (
        allNumbers &&
        sampleValues.some((v) => v !== null && v !== undefined)
      ) {
        return "number";
      }

      return "text";
    },
    [normalizedColumns, rows],
  );

  // Retorna os operadores disponíveis para um campo
  const getOperatorsForField = useCallback(
    (field) => {
      const type = getFieldType(field);
      return FILTER_OPERATORS[type] || FILTER_OPERATORS.text;
    },
    [getFieldType],
  );

  const getDeepValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => {
      if (acc && acc[part] !== undefined && acc[part] !== null) {
        return acc[part];
      }
      return "";
    }, obj);
  };
  // Função para aplicar operador de filtro
  const applyFilterOperator = (
    cellValue,
    filterValue,
    operator,
    fieldType,
    format,
  ) => {
    // Operadores que não precisam de valor
    if (operator === "isEmpty") {
      return cellValue === null || cellValue === undefined || cellValue === "";
    }
    if (operator === "isNotEmpty") {
      return cellValue !== null && cellValue !== undefined && cellValue !== "";
    }

    // Tratamento para boolean
    if (fieldType === "boolean") {
      const boolVal = Boolean(cellValue);
      if (operator === "isTrue") return boolVal === true;
      if (operator === "isFalse") return boolVal === false;
      return true;
    }

    // Para outros operadores, precisa de valor
    if (!filterValue || filterValue.trim() === "") return true;

    // Tratamento para datas
    if (
      fieldType === "date" ||
      format === "dd/MM/yyyy" ||
      format === "dd/MM/yyyy HH:mm:ss"
    ) {
      const parseDate = (val) => {
        if (!val) return null;
        // Tenta parsear dd/MM/yyyy
        const brMatch = String(val).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (brMatch) {
          return new Date(`${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`);
        }
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
      };

      const cellDate = parseDate(cellValue);
      const filterDate = parseDate(filterValue);

      if (!cellDate || !filterDate) return false;

      switch (operator) {
        case "equals":
          return cellDate.toDateString() === filterDate.toDateString();
        case "notEquals":
          return cellDate.toDateString() !== filterDate.toDateString();
        case "greaterThan":
          return cellDate > filterDate;
        case "greaterThanOrEqual":
          return cellDate >= filterDate;
        case "lessThan":
          return cellDate < filterDate;
        case "lessThanOrEqual":
          return cellDate <= filterDate;
        default:
          return true;
      }
    }

    // Tratamento para números
    if (fieldType === "number" || format === "BRL") {
      const cellNum = parseFloat(String(cellValue).replace(/[^\d.-]/g, ""));
      const filterNum = parseFloat(String(filterValue).replace(/[^\d.-]/g, ""));

      if (isNaN(cellNum) || isNaN(filterNum)) return false;

      switch (operator) {
        case "equals":
          return cellNum === filterNum;
        case "notEquals":
          return cellNum !== filterNum;
        case "greaterThan":
          return cellNum > filterNum;
        case "greaterThanOrEqual":
          return cellNum >= filterNum;
        case "lessThan":
          return cellNum < filterNum;
        case "lessThanOrEqual":
          return cellNum <= filterNum;
        default:
          return true;
      }
    }

    // Tratamento para texto (padrão)
    const cellStr = String(cellValue ?? "").toLowerCase();
    const filterStr = filterValue.toLowerCase();

    switch (operator) {
      case "contains":
        return cellStr.includes(filterStr);
      case "equals":
        return cellStr === filterStr;
      case "startsWith":
        return cellStr.startsWith(filterStr);
      case "endsWith":
        return cellStr.endsWith(filterStr);
      case "notEquals":
        return cellStr !== filterStr;
      default:
        return cellStr.includes(filterStr);
    }
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];

    const activeFilters = filters.filter((f) => {
      // Filtros que não precisam de valor
      if (f.operator === "isEmpty" || f.operator === "isNotEmpty") {
        return f.field;
      }
      if (f.operator === "isTrue" || f.operator === "isFalse") {
        return f.field;
      }
      return f.field && f.value && f.value.trim() !== "";
    });

    if (activeFilters.length === 0) return rows;

    return rows.filter((row) =>
      activeFilters.every((f) => {
        const colDef = normalizedColumns.find(
          (c) => c.field === f.field || c.dbField === f.field,
        );
        const realField = colDef?.dbField ?? f.field;
        const format = colDef?.format;
        const fieldType = getFieldType(f.field);
        const cellValue = row[realField];

        return applyFilterOperator(
          cellValue,
          f.value,
          f.operator || "contains",
          fieldType,
          format,
        );
      }),
    );
  }, [rows, filters, normalizedColumns, getFieldType]);

  const sortedRows = useMemo(() => {
    if (!sortField || !sortDir) return [...filteredRows];

    return [...filteredRows].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];

      if (va == null && vb == null) return 0;
      if (va == null) return sortDir === "asc" ? -1 : 1;
      if (vb == null) return sortDir === "asc" ? 1 : -1;

      // Data
      const da = new Date(va);
      const db = new Date(vb);
      if (!isNaN(da) && !isNaN(db)) {
        return sortDir === "asc" ? da - db : db - da;
      }

      // Número
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }

      // String
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [filteredRows, sortField, sortDir]);

  const totalRows = serverSide ? (rowCount ?? 0) : sortedRows.length;

  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageRows = serverSide
    ? sortedRows // aplicar sort também
    : sortedRows.slice(page * pageSize, page * pageSize + pageSize);

  const handleFilterTextChange = (text) => {
    dispatch(setFilterText({ gridId, filterText: text }));
    dispatch(setPage({ gridId, page: 0 }));
  };
  const handleFilterColumnChange = (col) => {
    dispatch(setFilterColumn({ gridId, filterColumn: col }));
    dispatch(setPage({ gridId, page: 0 }));
  };
  const handleSortChange = (field) => {
    let dir = "asc";
    if (sortField === field)
      dir = sortDir === "asc" ? "desc" : sortDir === "desc" ? null : "asc";
    dispatch(setSort({ gridId, sortField: dir ? field : null, sortDir: dir }));
    dispatch(setPage({ gridId, page: 0 }));
  };
  const handlePageChange = (newPage) => {
    dispatch(setPage({ gridId, page: newPage }));

    if (serverSide && onPageChangeServer) {
      onPageChangeServer(newPage, pageSize, { filters });
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const exists = prev.includes(id);
      const next = exists ? prev.filter((x) => x !== id) : [...prev, id];

      if (onSelectionChange) {
        if (selectionField) {
          const selectedValues = internalRows
            .filter((r) => next.includes(getRowId(r)))
            .map((r) => r[selectionField]);

          const changedValues = internalRows
            .filter((r) => r && getRowId(r) === id)
            .map((r) => r[selectionField]);

          // ⚙️ segue o padrão do MUI: primeiro = todos, segundo = alterados
          onSelectionChange(selectedValues, changedValues);
        } else {
          onSelectionChange(next, [id]);
        }
      }

      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const idsOnPage = pageRows.map(getRowId);
    const allSelected = idsOnPage.every((id) => selected.includes(id));
    const next = allSelected
      ? selected.filter((id) => !idsOnPage.includes(id))
      : Array.from(new Set([...selected, ...idsOnPage]));

    const changed = allSelected ? idsOnPage : idsOnPage;

    if (onSelectionChange) {
      if (selectionField) {
        const selectedValues = internalRows
          .filter((r) => next.includes(getRowId(r)))
          .map((r) => r[selectionField]);

        const changedValues = internalRows
          .filter((r) => changed.includes(getRowId(r)))
          .map((r) => r[selectionField]);

        onSelectionChange(selectedValues, changedValues);
      } else {
        onSelectionChange(next, changed);
      }
    }

    setSelected(next);
  };

  const startResize = (e, field) => {
    e.preventDefault();
    e.stopPropagation(); // Evita que o evento chegue ao th
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = colWidths[field];

    const onMouseMove = (ev) => {
      const newWidth = Math.max(40, startWidth + ev.clientX - startX);
      setColWidths((prev) => ({ ...prev, [field]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      // Pequeno delay para evitar que o click seja disparado
      setTimeout(() => setIsResizing(false), 100);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const hasFilters = (arr) => {
    if (!Array.isArray(arr)) return false;

    return arr.some((item) => item.value && item.value.trim() !== "");
  };

  const exportToExcel = async () => {
    const headers = normalizedColumns.map((c) => c.headerName || c.field);

    let rowsExport;
    if (!hasFilters(filters)) {
      rowsExport = await getRowsExport();
    }

    let dataRows = Array.isArray(rowsExport) ? rowsExport : filteredRows;

    // Detecta colunas numéricas (versão original sua)
    const numericColumns = normalizedColumns.reduce((acc, col) => {
      const isNumeric = dataRows.every((row) => {
        const val = row[col.dbField];

        if (val === null || val === "" || val === undefined) return true;
        const n = Number(val);
        return !isNaN(n) && isFinite(n);
      });

      if (isNumeric) acc[col.dbField] = true;
      return acc;
    }, {});

    // Função para converter string -> Date considerando formatos
    const parseDate = (value, format) => {
      if (!value) return null;

      // dd/MM/yyyy HH:mm:ss
      if (format === "dd/MM/yyyy HH:mm:ss") {
        const regex = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;
        const match = value.match(regex);
        if (match) {
          const [, dd, mm, yyyy, HH, MM, SS] = match;
          return new Date(`${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`);
        }
      }

      // dd/MM/yyyy
      if (format === "dd/MM/yyyy") {
        const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const match = value.match(regex);
        if (match) {
          const [, dd, mm, yyyy] = match;
          return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
        }
      }

      // fallback (ISO, timestamps)
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    // Monta AOA
    const data = dataRows.map((r) =>
      normalizedColumns.map((c) => {
        const value = r[c.dbField];

        // 👉 TRATAR DATAS
        if (c.format === "dd/MM/yyyy HH:mm:ss" || c.format === "dd/MM/yyyy") {
          const d = parseDate(value, c.format);
          return d instanceof Date && !isNaN(d.getTime()) ? d : value;
        }

        // NÚMEROS
        if (numericColumns[c.dbField]) {
          const num = Number(value);
          return isNaN(num) ? null : num;
        }

        // BRL
        if (c.format === "BRL") {
          const num = Number(value);
          return isNaN(num) ? value : num;
        }

        // demais formatos
        return formatValue(value, c.format);
      }),
    );

    // Criar Sheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    // Formatar colunas de data no Excel
    normalizedColumns.forEach((c, colIdx) => {
      if (c.format !== "dd/MM/yyyy" && c.format !== "dd/MM/yyyy HH:mm:ss") {
        return;
      }

      for (let r = 2; r <= data.length + 1; r++) {
        const cellAddress = XLSX.utils.encode_cell({ r: r - 1, c: colIdx });
        const cell = ws[cellAddress];
        if (!cell) continue;

        if (cell.v instanceof Date) {
          cell.t = "d";
          cell.z = c.format; // Excel interpreta perfeitamente
        }
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dados");

    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
        type: "application/octet-stream",
      }),
      "dados_exportados.xlsx",
    );
  };

  const handleFilterBlur = () => {
    if (!serverSide) return;

    const activeFilters = filters.filter(
      (f) => f.field && f.value && f.value.trim() !== "",
    );

    onPageChangeServer(page, pageSize, {
      filters: activeFilters.map((f) => {
        const colDef = normalizedColumns.find((c) => c.field === f.field);
        return {
          field: colDef?.dbField ?? f.field,
          value: f.value,
        };
      }),
    });
  };

  return (
    <div
      className={`w-full overflow-auto bg-white rounded shadow-sm p-2 apple-grid ${className}`}
      style={{
        maxHeight: tableHeight,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="filter-container">
        <div className="filter-label">Filtros</div>
        <div className="filter-row">
          <select
            className="filter-field"
            value={filters[0]?.field || ""}
            onChange={(e) => handleFilterFieldChange(0, e.target.value)}
          >
            <option value="">Campo...</option>
            {normalizedColumns.map((col) => (
              <option key={col.field} value={col.dbField}>
                {col.headerName || col.field}
              </option>
            ))}
          </select>

          <select
            className="filter-operator"
            value={filters[0]?.operator || "contains"}
            onChange={(e) => handleFilterOperatorChange(0, e.target.value)}
            style={{ minWidth: 120 }}
          >
            {getOperatorsForField(filters[0]?.field).map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>

          <div className="filter-value-group">
            {filters[0]?.operator !== "isEmpty" &&
              filters[0]?.operator !== "isNotEmpty" &&
              filters[0]?.operator !== "isTrue" &&
              filters[0]?.operator !== "isFalse" && (
                <input
                  className="filter-value"
                  type={
                    getFieldType(filters[0]?.field) === "date" ? "date" : "text"
                  }
                  placeholder="Valor..."
                  value={filters[0]?.value || ""}
                  onChange={(e) => handleFilterValueChange(0, e.target.value)}
                  onBlur={() => handleFilterBlur()}
                  onPaste={() => {
                    setTimeout(() => handleFilterBlur(), 50); // CTL+V FIX
                  }}
                />
              )}

            <div className="filter-buttons">
              <button
                type="button"
                className="filter-add"
                onClick={handleAddFilter}
                title="Adicionar filtro"
              >
                +
              </button>
              {filters.length > 1 && (
                <button
                  type="button"
                  className="filter-remove"
                  onClick={() => handleRemoveFilter(filters.length - 1)}
                  title="Remover último filtro"
                >
                  −
                </button>
              )}
            </div>

            <div className="filter-export" style={{ display: "flex", gap: 16 }}>
              <div style={{ textAlign: "center", margin: "3px" }}>
                <button
                  className="excel-btn"
                  onClick={exportToExcel}
                  title="Exportar dados da Tela"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 10, margin: 5, color: "#444" }}>
                    Exportar Tela
                  </span>
                  <DescriptionIcon sx={{ fontSize: 20, color: "#1b5e20" }} />

                  <span
                    className="muted"
                    style={{ alignSelf: "center", margin: 5, fontSize: 10 }}
                  >
                    {!temFiltro ? totalRows : filteredRows.length} registro(s)
                  </span>
                </button>
              </div>
              {loading ? <CircularProgress /> : false}
            </div>
          </div>
        </div>

        {/* Linha 2 (e demais, caso haja mais filtros) */}
        {filters.slice(1).map((f, i) => (
          <div key={i + 1} className="filter-row">
            <select
              className="filter-field"
              value={f.field}
              onChange={(e) => handleFilterFieldChange(i + 1, e.target.value)}
            >
              <option value="">Campo...</option>
              {normalizedColumns.map((col) => (
                <option key={col.field} value={col.field}>
                  {col.headerName || col.field}
                </option>
              ))}
            </select>

            <select
              className="filter-operator"
              value={f.operator || "contains"}
              onChange={(e) =>
                handleFilterOperatorChange(i + 1, e.target.value)
              }
              style={{ minWidth: 120 }}
            >
              {getOperatorsForField(f.field).map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>

            {f.operator !== "isEmpty" &&
              f.operator !== "isNotEmpty" &&
              f.operator !== "isTrue" &&
              f.operator !== "isFalse" && (
              <input
                className="filter-value"
                type={getFieldType(f.field) === "date" ? "date" : "text"}
                placeholder="Valor..."
                value={f.value}
                onChange={(e) => handleFilterValueChange(i + 1, e.target.value)}
                onBlur={() => handleFilterBlur()}
                onPaste={() => {
                  setTimeout(() => handleFilterBlur(), 50); // CTL+V FIX
                }}
              />
            )}
          </div>
        ))}

        {/* Botão Limpar */}
        {temFiltro && (
          <div className="filter-row" style={{ justifyContent: "flex-start" }}>
            <button
              type="button"
              className="filter-clear"
              onClick={handleClearFilters}
              title="Limpar todos os filtros"
            >
              Limpar
            </button>
          </div>
        )}
      </div>

      <div className="table-container">
        <div style={{ marginBottom: 10 }}>
          <details>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Colunas
            </summary>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                paddingLeft: 15,
                marginTop: 5,
              }}
            >
              {normalizedColumns.map((col) => (
                <label key={col.field} style={{ marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={
                      Array.isArray(visibleColumns) &&
                      visibleColumns.includes(col.field)
                    }
                    onChange={() => toggleColumnVisibility(col.field)}
                  />{" "}
                  {col.headerName || col.field}
                </label>
              ))}
            </div>
          </details>
        </div>

        <table>
          <thead>
            <tr>
              {checkboxSelection && (
                <th style={{ width: "40px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={
                      pageRows.length > 0 &&
                      pageRows.every((r) => selected.includes(getRowId(r)))
                    }
                    onChange={toggleSelectAllOnPage}
                  />
                </th>
              )}
              {normalizedColumns
                .filter((c) =>
                  Array.isArray(visibleColumns)
                    ? visibleColumns.includes(c.field)
                    : true,
                )
                .map((col) => {
                  const headerTextAlign =
                    col.headerAlign ?? col.align ?? "left";
                  return (
                    <th
                      key={col.field}
                      onClick={(e) => {
                        if (isResizing) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        handleSortChange(col.field);
                      }}
                      onMouseEnter={() => setHoveredCol(col.field)}
                      onMouseLeave={() => setHoveredCol(null)}
                      className={hoveredCol === col.field ? "hovered" : ""}
                      style={{
                        textAlign: headerTextAlign,
                        cursor: col.sortable ? "pointer" : "default",
                        width: `${colWidths[col.field]}px`,
                        minWidth: `40px`,
                        position: "relative",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <div
                        className={`header-with-tooltip ${
                          col.headerAlign === "center"
                            ? "center"
                            : col.headerAlign === "right"
                              ? "right"
                              : ""
                        }`}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>{col.headerName ?? col.field}</span>
                          <span className="sort-indicator">
                            {sortField === col.field
                              ? sortDir === "asc"
                                ? "▲"
                                : "▼"
                              : "⇅"}
                          </span>
                        </span>

                        {col.description && (
                          <span className="cg-tooltip">{col.description}</span>
                        )}

                        <div
                          className="resize-handle"
                          onMouseDown={(e) => startResize(e, col.field)}
                        />
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>

          <tbody>
            {pageRows.length > 0 ? (
              pageRows.map((row) => {
                const id = getRowId(row);
                return (
                  <tr
                    key={id}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={selected.includes(id) ? "selected" : ""}
                  >
                    {checkboxSelection && (
                      <td style={{ width: "40px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selected.includes(id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelect(id);
                          }}
                        />
                      </td>
                    )}
                    {normalizedColumns
                      .filter((c) =>
                        Array.isArray(visibleColumns)
                          ? visibleColumns.includes(c.field)
                          : true,
                      )
                      .map((col) => {
                        const cellValue = row[col.field];
                        const textAlign = col.align ?? col.align ?? "left";
                        const display = col.renderCell
                          ? col.renderCell({
                              row,
                              value: cellValue,
                              field: col.field,
                            })
                          : formatValue(cellValue, col.format);
                        return (
                          <td
                            key={col.field}
                            style={{
                              textAlign: textAlign,
                              width: `${colWidths[col.field]}px`,
                              minWidth: `40px`,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              cursor: col.clickable ? "pointer" : "default",
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // evita conflitos com seleção
                              if (col.clickable && onRowClick) {
                                onRowClick(row);
                              }
                            }}
                          >
                            {display}
                          </td>
                        );
                      })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    (checkboxSelection ? 1 : 0) + normalizedColumns.length
                  }
                  className="text-center muted"
                >
                  Nenhum registro encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination">
          <div>
            Mostrando {page * pageSize + 1} -
            {Math.min((page + 1) * pageSize, totalRows)} de {totalRows}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => handlePageChange(0)} disabled={page === 0}>
              «
            </button>
            <button
              onClick={() => handlePageChange(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              ‹
            </button>
            <span>
              Página {page + 1} / {pageCount}
            </span>
            <button
              onClick={() =>
                handlePageChange(Math.min(pageCount - 1, page + 1))
              }
              disabled={page >= pageCount - 1}
            >
              ›
            </button>
            <button
              onClick={() => handlePageChange(pageCount - 1)}
              disabled={page >= pageCount - 1}
            >
              »
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
